import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/auth';
import { getActiveSDRs } from '../../lib/api';
import { getDayAppointments, getCreatedOnDate, contractTypeOf, type DayRdv } from '../../lib/refonteApi';
import { type LifecycleStatus } from '../../lib/appointmentLifecycle';
import StatusBadge from '../../components/refonte/StatusBadge';
import ContractBadge from '../../components/refonte/ContractBadge';
import RdvDetail from '../../components/refonte/RdvDetail';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const RDV_TARGET = 3;
const toISODate = (d: Date) => d.toISOString().split('T')[0];
const fmtDay = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });

const Stat: React.FC<{ value: React.ReactNode; label: string; tone?: string }> = ({ value, label, tone }) => (
  <div className="text-center px-3 first:pl-0">
    <div className={`font-mono text-[18px] font-bold leading-none ${tone || ''}`}>{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{label}</div>
  </div>
);

const DayToDayPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'admin';
  const [date, setDate] = useState<Date>(new Date());
  const [rows, setRows] = useState<DayRdv[]>([]);
  const [created, setCreated] = useState<DayRdv[]>([]);
  const [sdrs, setSdrs] = useState<Array<{ id: string; name: string; client_name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);       // fiche section ①
  const [prodSelId, setProdSelId] = useState<string | null>(null); // fiche section ②
  const dateStr = toISODate(date);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [day, cre, active] = await Promise.all([
        getDayAppointments(dateStr), getCreatedOnDate(dateStr), getActiveSDRs(date),
      ]);
      setRows(day); setCreated(cre);
      setSdrs((active ?? []).map((s: any) => ({ id: s.sdr_id, name: s.sdr_name, client_name: s.client_name })));
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [dateStr, date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ch = supabase.channel(`d2d_${dateStr}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments', filter: `date=eq.${dateStr}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [dateStr, load]);

  // Sélection par défaut : 1er RDV de chaque section (jamais de panneau vide).
  useEffect(() => { if (rows.length && !rows.some(r => r.id === selId)) setSelId(rows[0].id); }, [rows, selId]);
  useEffect(() => { if (created.length && !created.some(r => r.id === prodSelId)) setProdSelId(created[0].id); }, [created, prodSelId]);

  const shiftDay = (delta: number) => {
    const d = new Date(date); d.setDate(d.getDate() + delta);
    if (d.getDay() === 0) d.setDate(d.getDate() + (delta > 0 ? 1 : -2));
    if (d.getDay() === 6) d.setDate(d.getDate() + (delta > 0 ? 2 : -1));
    setDate(d); setSelId(null); setProdSelId(null);
  };

  const countBy = (s: LifecycleStatus) => rows.filter(r => r.lifecycle_status === s).length;
  const pendingConfirm = rows.filter(r => r.lifecycle_status == null || r.lifecycle_status === 'posed').length;
  const allProcessed = rows.length > 0 && pendingConfirm === 0;
  // RDV dont l'heure est passée mais sans résultat (encore posé/confirmé) → à statuer.
  const nowTs = Date.now();
  const isPast = (r: DayRdv) => !!r.date && new Date(`${r.date}T${(r.time || '23:59').slice(0, 5)}`).getTime() < nowTs;
  const isPending = (r: DayRdv) => r.lifecycle_status == null || r.lifecycle_status === 'posed' || r.lifecycle_status === 'confirmed';
  const toFollowUp = rows.filter(r => isPending(r) && isPast(r));
  const sel = rows.find(r => r.id === selId) || null;
  const prodSel = created.find(r => r.id === prodSelId) || rows.find(r => r.id === prodSelId) || null;

  const ranked = sdrs
    .map(s => ({ ...s, mine: created.filter(c => c.sdr_id === s.id) }))
    .sort((a, b) => b.mine.length - a.mine.length);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Day to Day</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftDay(-1)} aria-label="Jour précédent" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">‹</button>
          <span className="font-mono text-[12px] text-dim capitalize min-w-[180px] text-center">{fmtDay(date)}</span>
          <button onClick={() => shiftDay(1)} aria-label="Jour suivant" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">›</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-5">Rappels du matin & confirmation, puis production · données réelles · temps réel {loading && '· maj…'}</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 font-mono">{err}</div>}

      {toFollowUp.length > 0 && (
        <button onClick={() => setSelId(toFollowUp[0].id)}
          className="mb-4 w-full flex items-center gap-2 rounded-lg bg-st-warn/10 border border-st-warn/25 text-st-warn px-3 py-2 text-[13px] font-semibold text-left hover:bg-st-warn/15 transition">
          ⏰ {toFollowUp.length} RDV dont l'heure est passée sans résultat — à statuer (effectué / no-show). En cas de doute, demande au client.
        </button>
      )}

      {allProcessed && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-st-ok/10 text-st-ok px-3 py-2 text-[13px] font-semibold">
          ✓ Tous les RDV du jour sont traités — place à la production.
        </div>
      )}

      <div className="flex flex-col gap-4">
        {/* ── Section ① Confirmation ── */}
        <div className={`grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 lg:h-[78vh] items-stretch ${allProcessed ? 'order-3' : 'order-1'}`}>
          <div className="bg-panel border border-line rounded-card p-5 flex flex-col overflow-hidden">
            <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3 shrink-0">① RDV du jour — à confirmer (1ère tâche SDR)</h2>
            <div className="flex divide-x divide-line border border-line rounded-lg py-2.5 mb-4 bg-panel-2 shrink-0">
              <Stat value={rows.length} label="RDV du jour" />
              <Stat value={pendingConfirm} label="À confirmer" tone={pendingConfirm ? 'text-st-warn' : 'text-st-ok'} />
              <Stat value={countBy('confirmed')} label="Confirmés" tone="text-st-cy" />
              <Stat value={countBy('held')} label="Effectués" tone="text-st-ok" />
              <Stat value={countBy('no_show')} label="No-show" tone={countBy('no_show') ? 'text-st-bad' : ''} />
            </div>
            <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loading ? <SkeletonRows count={5} /> : rows.length === 0 ? (
              <EmptyState title="Aucun RDV ce jour" hint="Change de date avec les flèches ci-dessus." />
            ) : (
              <div className="divide-y divide-line">
                {rows.map(rdv => (
                  <button key={rdv.id} onClick={() => setSelId(rdv.id)}
                    className={`w-full text-left py-3 flex items-center gap-3 flex-wrap hover:bg-panel-2 transition rounded-lg px-2 -mx-2 ${selId === rdv.id ? 'bg-panel-2 ring-1 ring-line-2' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate">
                        {rdv.time?.slice(0, 5)} · {rdv.prospect_name || 'Prospect'}
                        {rdv.company_name && <span className="text-dim font-normal"> · {rdv.company_name}</span>}
                      </div>
                      <div className="font-mono text-[10px] text-faint">{rdv.clients?.name || '—'} · SDR {rdv.sdr?.sales_name || '—'}</div>
                    </div>
                    {isPending(rdv) && isPast(rdv) && <span title="Heure passée — à statuer" className="text-st-warn text-[13px]">⏰</span>}
                    <ContractBadge contractType={contractTypeOf(rdv)} />
                    <StatusBadge status={rdv.lifecycle_status ?? null} />
                  </button>
                ))}
              </div>
            )}
            </div>
          </div>

          <div className="bg-panel border border-line rounded-card p-5 overflow-y-auto">
            {sel ? <RdvDetail rdv={sel} role={role} onChanged={load} />
              : <div className="h-full grid place-items-center"><EmptyState title="Sélectionne un RDV" hint="Clique une ligne pour la fiche, confirmer, appeler ou reporter." /></div>}
          </div>
        </div>

        {/* Séparateur entre les deux phases (reste au milieu même après inversion) */}
        <div className="order-2 flex items-center gap-3 py-1" aria-hidden>
          <div className="h-px flex-1 bg-line-2" />
          <span className="font-mono text-[9px] uppercase tracking-widest text-faint">
            {allProcessed ? '↓ Confirmation du jour' : '↓ Production de la journée'}
          </span>
          <div className="h-px flex-1 bg-line-2" />
        </div>

        {/* ── Section ② Production (SDR classés + fiche latérale) ── */}
        <div className={`grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 lg:h-[78vh] items-stretch ${allProcessed ? 'order-1' : 'order-3'}`}>
          <div className="bg-panel border border-line rounded-card p-5 flex flex-col overflow-hidden">
            <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3 shrink-0">② Production SDR · prise de RDV · objectif {RDV_TARGET}/jour</h2>
            <div className="inline-flex divide-x divide-line border border-line rounded-lg py-2.5 mb-4 bg-panel-2 shrink-0 self-start">
              <Stat value={sdrs.length} label="SDR en prod" />
              <Stat value={created.length} label="RDV posés auj." tone="text-hook-rose" />
              <Stat value={sdrs.length * RDV_TARGET} label="Objectif" />
              <Stat value={created.filter(c => c.lifecycle_status === 'held').length} label="Déjà effectués" tone="text-st-ok" />
            </div>
            <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loading ? <SkeletonRows count={3} /> : ranked.length === 0 ? (
              <EmptyState title="Aucun SDR planifié" hint="Personne en journée de prospection ce jour." />
            ) : (
              <div className="space-y-2.5">
                {ranked.map((s, i) => {
                  const pct = Math.min(100, Math.round((s.mine.length / RDV_TARGET) * 100));
                  const done = s.mine.length >= RDV_TARGET;
                  return (
                    <div key={s.id} className="border border-line rounded-lg p-3 flex flex-col md:flex-row md:items-center gap-3">
                      <div className="md:w-56 shrink-0">
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-[13px] font-semibold truncate"><span className="font-mono text-faint mr-1.5">#{i + 1}</span>{s.name}</span>
                          <span className="font-mono text-[11px] text-dim">{s.mine.length}/{RDV_TARGET}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-raise overflow-hidden">
                          <div className={`h-full rounded-full ${done ? 'bg-st-ok' : 'bg-hook-rose'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="font-mono text-[9.5px] text-faint mt-0.5 truncate">{s.client_name}</div>
                      </div>
                      <div className="flex-1 flex flex-wrap gap-1.5">
                        {s.mine.length === 0 ? (
                          <span className="text-[11px] text-faint">Aucun RDV posé encore.</span>
                        ) : s.mine.map(m => (
                          <button key={m.id} onClick={() => setProdSelId(m.id)}
                            className={`inline-flex items-center gap-1.5 text-[11.5px] border rounded-lg px-2 py-1 transition ${prodSelId === m.id ? 'border-ink bg-panel-2' : 'border-line hover:border-ink hover:bg-panel-2'}`}>
                            <span className="font-mono text-faint">{m.time?.slice(0, 5) || '—'}</span>
                            <span className="truncate max-w-[120px]">{m.prospect_name || 'Prospect'}</span>
                            <StatusBadge status={m.lifecycle_status ?? null} />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>

          <div className="bg-panel border border-line rounded-card p-5 overflow-y-auto">
            {prodSel ? <RdvDetail rdv={prodSel} role={role} onChanged={load} bookingFirst />
              : <div className="h-full grid place-items-center"><EmptyState title="Sélectionne un RDV posé" hint="Clique un RDV dans la liste d'un SDR pour voir sa fiche." /></div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayToDayPage;
